import { fetchScript } from "~/recipal-app/controllers/shared/requests";
import { computePosition, offset, flip, shift, autoUpdate } from "@floating-ui/dom";
import { once } from "~/jose_modern/once";
import { handlePossiblePaginationClick } from "../pagination_handler";
import {
  promptForUnsavedChanges,
  NAVIGATION_CANCELLED_EVENT,
} from "~/jose_modern/warn_unsaved_trigger";
import { URL_QUERY_PARAMS, startSpinner, stopSpinner } from "./common";
import * as editing from "./editing";

const INVENTORY_CONTAINER_SELECTOR = ".inventory-inner-container";

let pendingUrlUpdate = null;
let filterLoadingTimeoutId = null;

// =============================================================================
// TAG FILTERING
// =============================================================================

const updateTagVisualState = (tag, isActive) => {
  const filterColor = tag.dataset.filterColor;

  if (isActive) {
    tag.classList.remove("is-outlined", "has-text-weight-semibold");
    tag.style.backgroundColor = filterColor;
    tag.style.color = "white";
    tag.style.borderColor = "transparent";
  } else {
    tag.classList.add("is-outlined", "has-text-weight-semibold");
    tag.style.borderColor = filterColor;
    tag.style.color = filterColor;
    tag.style.backgroundColor = "white";
  }
};

const updateFilterTagStates = () => {
  const urlParams = new URLSearchParams(window.location.search);

  // Support both array format and legacy single-value format
  const activeIngredientTags = urlParams
    .getAll("ingredient_tags[]")
    .concat(urlParams.has("ingredient_tag") ? [urlParams.get("ingredient_tag")] : []);
  const activeSupplierId = urlParams.get("supplier_filter_id");
  const activeRecipeTags = urlParams
    .getAll("recipe_tags[]")
    .concat(urlParams.has("recipe_tag") ? [urlParams.get("recipe_tag")] : []);

  const allFilterTags = document.querySelectorAll("[data-filter-type]");

  allFilterTags.forEach((tag) => {
    const filterType = tag.dataset.filterType;
    const filterValue = String(tag.dataset.filterValue);

    let isActive = false;
    if (filterType === "ingredient_tags" && activeIngredientTags.includes(filterValue)) {
      isActive = true;
    } else if (filterType === "supplier_filter_id" && filterValue === activeSupplierId) {
      isActive = true;
    } else if (filterType === "recipe_tags" && activeRecipeTags.includes(filterValue)) {
      isActive = true;
    }

    updateTagVisualState(tag, isActive);
  });
};

export const initializeTagClick = () => {
  once(document.body, "tag-click-delegate", () => {
    const handler = (e) => {
      if (e.target.closest("a.tag")) {
        const searchButton = document.querySelector(
          'form[data-filter-form] input[type="submit"], form[data-filter-form] button[type="submit"]'
        );
        if (searchButton) {
          searchButton.classList.add("is-search-loading");
          searchButton.disabled = true;
        }
      }
    };
    document.addEventListener("click", handler, true);
  });
};

// =============================================================================
// MORE UTILITY FUNCTIONS
// =============================================================================

const ajaxRequest = async (url) => {
  pendingUrlUpdate = url;
  startSpinner();
  try {
    await fetchScript(url, { method: "GET" });
  } catch (error) {
    console.error("Error loading filtered content:", error);
    stopSpinner(); // Ensure spinner stops on error
  }
};

const mergeUrlWithCurrent = (href) => {
  const currentParams = new URLSearchParams(window.location.search);
  const linkUrl = new URL(href, window.location.origin);
  const basePath = href.split("?")[0];
  const newUrl = new URL(basePath, window.location.origin);

  const isArrayParam = (key) => key.endsWith("[]");

  for (const [key, value] of currentParams) {
    if (isArrayParam(key)) {
      newUrl.searchParams.append(key, value);
    } else {
      newUrl.searchParams.set(key, value);
    }
  }

  for (const [key, value] of linkUrl.searchParams) {
    if (isArrayParam(key)) {
      newUrl.searchParams.append(key, value);
    } else {
      newUrl.searchParams.set(key, value);
    }
  }

  return newUrl;
};

const isInventoryAdjustmentNav = (href) => {
  try {
    const u = new URL(href, location.origin);
    for (const [k] of u.searchParams) if (URL_QUERY_PARAMS.has(k)) return true;
  } catch (err) {
    console.error(err);
  }
  return false;
};

const shouldProceedWithNavigation = () => {
  const hasUnsavedChanges = editing.checkHasUnsavedChanges();
  if (hasUnsavedChanges) {
    if (!promptForUnsavedChanges()) {
      document.dispatchEvent(new CustomEvent(NAVIGATION_CANCELLED_EVENT));
      return false;
    }
  }
  return true;
};

// We need to ensure that filters/search/sorting are additive and do not clobber each other.
// Current approach is very URL-focused, since we were already using URL params for all the
// filtering/search/sorting actions.
export const initializeUrlQueryParamHandling = () => {
  // Handle navigation (click or keyboard) on all remote links
  const handleRemoteLinkNavigation = async (e) => {
    const link = e.target.closest("a[data-remote='true'], a[data-remote]");
    if (!link) return;

    const inventoryContainer = link.closest(INVENTORY_CONTAINER_SELECTOR);
    if (!inventoryContainer) return;

    const href = link.getAttribute("href");
    if (!href || !isInventoryAdjustmentNav(href)) return; // let UJS handle unrelated remote links

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Check for unsaved changes and prompt user
    if (!shouldProceedWithNavigation()) return;

    const currentSortParams = getSortParams(window.location.href);
    const linkSortParams = getSortParams(href);

    // Check if this action actually changes the sort parameters.
    const sortColumnChanged = currentSortParams.sortColumn !== linkSortParams.sortColumn;
    const sortDirectionChanged = currentSortParams.sortDirection !== linkSortParams.sortDirection;
    const sortHasChanged = sortColumnChanged || sortDirectionChanged;

    // If this is marked as an optimistic sort link, update UI immediately
    if (link.dataset.optimisticSort) {
      const sortControl = document.getElementById("inventory-sort-control");
      if (sortControl) {
        const sortLabel = link.dataset.sortLabel || linkSortParams.sortColumn || "";
        updateSortDisplay({ sortControl, ...linkSortParams, sortLabel });
        showSortingShimmer(sortControl);
      }

      const dropdownMenu = link.closest(".dropdown-menu.sort-dropdown-menu");
      if (dropdownMenu) {
        const dropdownContainer = dropdownMenu.closest(".dropdown");
        const dropdownTrigger = dropdownContainer?.querySelector(".sort-menu-trigger");
        dropdownContainer?.classList.remove("is-active");
        dropdownMenu.style.display = "none";
        if (dropdownTrigger) {
          dropdownTrigger.style.pointerEvents = "none";
          dropdownTrigger.style.opacity = "0.5";
        }
      }
    }

    const currentParams = new URLSearchParams(window.location.search);
    const basePath = link.href.split("?")[0];
    let finalUrl;

    if (link.classList.contains("reset-filters")) {
      const newUrl = new URL(basePath, window.location.origin);
      const adjustmentType = currentParams.get("adjustment_type");
      if (adjustmentType) {
        newUrl.searchParams.set("adjustment_type", adjustmentType);
      }
      finalUrl = newUrl.toString();

      document.querySelectorAll("input[name='search']").forEach((field) => {
        field.value = "";
      });
    } else if (link.dataset.filterType) {
      const merged = mergeUrlWithCurrent(href);

      const filterType = link.dataset.filterType;
      const filterValue = String(link.dataset.filterValue);

      if (filterType === "ingredient_tags" || filterType === "recipe_tags") {
        const paramKey = `${filterType}[]`;
        const currentValues = merged.searchParams.getAll(paramKey);

        // Clear legacy single-value params
        merged.searchParams.delete(filterType.replace(/s$/, "")); // ingredient_tag or recipe_tag

        if (currentValues.includes(filterValue)) {
          merged.searchParams.delete(paramKey);
          currentValues
            .filter((v) => v !== filterValue)
            .forEach((v) => {
              merged.searchParams.append(paramKey, v);
            });
        } else {
          merged.searchParams.append(paramKey, filterValue);
        }
      } else {
        // Single-value filters (supplier_filter_id) - existing behavior
        if (currentParams.get(filterType) === filterValue) {
          merged.searchParams.delete(filterType);
        } else {
          merged.searchParams.set(filterType, filterValue);
        }
      }

      merged.searchParams.delete("page");

      document.querySelectorAll("[data-filter-type]").forEach((tag) => {
        const tagType = tag.dataset.filterType;
        const tagValue = String(tag.dataset.filterValue);
        let isActive;

        if (tagType === "ingredient_tags" || tagType === "recipe_tags") {
          isActive = merged.searchParams.getAll(`${tagType}[]`).includes(tagValue);
        } else {
          isActive = merged.searchParams.get(tagType) === tagValue;
        }
        updateTagVisualState(tag, isActive);
      });

      finalUrl = merged.toString();
    } else {
      const merged = mergeUrlWithCurrent(href);

      if (sortHasChanged) {
        merged.searchParams.delete("page");
      }

      finalUrl = merged.toString();

      // If it's a pagination link, mark it for `handlePaginationScrolling`
      handlePossiblePaginationClick(link);
    }

    await ajaxRequest(finalUrl);
  };

  // Listen for both click and keyboard activation
  document.addEventListener(
    "click",
    handleRemoteLinkNavigation,
    true // Use capture phase to intercept before Rails UJS
  );
  document.addEventListener(
    "keydown",
    (e) => {
      // Handle Enter key on links (reminder: Space is handled by browser as click)
      if (e.key === "Enter") {
        handleRemoteLinkNavigation(e).catch(console.error);
      }
    },
    true // Use capture phase
  );

  const showFilteringLoadingState = () => {
    const searchButton = document.querySelector(
      'form[data-filter-form] input[type="submit"], form[data-filter-form] button[type="submit"]'
    );

    if (!searchButton) return;

    const clearLoadingTimeout = () => {
      if (filterLoadingTimeoutId) {
        clearTimeout(filterLoadingTimeoutId);
        filterLoadingTimeoutId = null;
      }
    };

    clearLoadingTimeout();

    searchButton.classList.add("is-search-loading");
    searchButton.disabled = true;

    const removeLoadingState = () => {
      searchButton.classList.remove("is-search-loading");
      searchButton.disabled = false;
      clearLoadingTimeout();
    };

    document.addEventListener("partial:replace", removeLoadingState, { once: true });
    filterLoadingTimeoutId = setTimeout(removeLoadingState, 5000);
  };

  // Handle search form submissions with change detection
  document.addEventListener(
    "submit",
    async (e) => {
      const filterForm = e.target.closest("form[data-filter-form]");
      if (!filterForm) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Check for unsaved changes and prompt user
      if (!shouldProceedWithNavigation()) return;

      const formData = new FormData(filterForm);
      const currentSearchValue = (formData.get("search") || "").trim();
      const urlParams = new URLSearchParams(window.location.search);
      const urlSearchValue = urlParams.get("search") || "";

      if ((!currentSearchValue && !urlSearchValue) || currentSearchValue === urlSearchValue) {
        return;
      }

      showFilteringLoadingState();

      const action = filterForm.getAttribute("action");
      const newUrl = new URL(action, window.location.origin);

      for (const [key, value] of urlParams) {
        if (key !== "search" && key !== "page") {
          if (key.endsWith("[]")) {
            newUrl.searchParams.append(key, value);
          } else {
            newUrl.searchParams.set(key, value);
          }
        }
      }

      if (currentSearchValue) {
        newUrl.searchParams.set("search", currentSearchValue);
      }

      const adjustmentType = formData.get("adjustment_type");
      if (adjustmentType) {
        newUrl.searchParams.set("adjustment_type", adjustmentType.toString());
      }

      const includeArchived = formData.get("include_archived");
      if (includeArchived) {
        newUrl.searchParams.set("include_archived", includeArchived.toString());
      }

      await ajaxRequest(newUrl.toString());
    },
    true // Use capture phase to intercept before Rails UJS
  );

  // Handle include_archived checkbox changes
  document.addEventListener("change", async (e) => {
    if (!e.target.matches("input[name='include_archived']")) return;
    const checkbox = e.target;

    const inventoryContainer = checkbox.closest(INVENTORY_CONTAINER_SELECTOR);
    if (!inventoryContainer) return;

    // Check for unsaved changes and prompt user
    if (!shouldProceedWithNavigation()) {
      checkbox.checked = !checkbox.checked;
      return;
    }

    const newUrl = new URL(window.location.href);

    if (checkbox.checked) {
      newUrl.searchParams.set("include_archived", "1");
    } else {
      newUrl.searchParams.delete("include_archived");
    }

    newUrl.searchParams.delete("page");

    await ajaxRequest(newUrl.toString());
  });
};

export const buildFilterSummaryAndCleanParams = (isFinishedGoods) => {
  const labels = [];
  const urlParams = new URLSearchParams(window.location.search);

  const pushLabelsForArrayParam = (filterType, paramKey) => {
    // Support both array format and legacy single-value
    const values = urlParams
      .getAll(`${paramKey}[]`)
      .concat(
        urlParams.has(paramKey.replace(/s$/, "")) ? [urlParams.get(paramKey.replace(/s$/, ""))] : []
      );

    values.forEach((value) => {
      const candidates = document.querySelectorAll(`[data-filter-type="${filterType}"]`);
      const match = Array.from(candidates).find(
        (el) => String(el.dataset.filterValue) === String(value)
      );
      const text = match?.textContent?.trim();
      if (text && !labels.includes(text)) labels.push(text);
    });
  };

  const pushLabelFor = (filterType, value) => {
    if (!value) return;
    const candidates = document.querySelectorAll(`[data-filter-type="${filterType}"]`);
    const match = Array.from(candidates).find(
      (el) => String(el.dataset.filterValue) === String(value)
    );
    const text = match?.textContent?.trim();
    if (text) labels.push(text);
  };

  if (isFinishedGoods) {
    pushLabelsForArrayParam("recipe_tags", "recipe_tags");
  } else {
    pushLabelsForArrayParam("ingredient_tags", "ingredient_tags");
    pushLabelFor("supplier_filter_id", urlParams.get("supplier_filter_id"));
  }

  if (urlParams.get("include_archived")) {
    if (document.getElementById("include_archived")) {
      labels.push("including archived");
    } else {
      // Strip stale param so copied URLs don't carry it to pages without archiving
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("include_archived");
      window.history.replaceState({}, "", cleanUrl.pathname + cleanUrl.search);
    }
  }

  if (labels.length > 0) return `Filtering: ${labels.join(", ")}`;
  return "Filter";
};

export const initializeFilterToggle = (context) => {
  const toggleLink = document.getElementById("toggle-tag-filters");
  const filterDiv = document.getElementById("all-tag-filters");
  if (!toggleLink || !filterDiv) return;

  const isFinishedGoods = context.resource === "finished_goods";

  const hideText = "Hide Filters";

  const newToggleLink = toggleLink.cloneNode(true);
  toggleLink.parentNode.replaceChild(newToggleLink, toggleLink);

  const newToggleText = document.getElementById("toggle-tag-filter-text");

  const syncToggleState = (isHidden) => {
    if (newToggleText)
      newToggleText.textContent = isHidden
        ? buildFilterSummaryAndCleanParams(isFinishedGoods)
        : hideText;
    newToggleLink.setAttribute("aria-expanded", String(!isHidden));
    const chevron = newToggleLink.querySelector(".fa-caret-down, .fa-caret-up");
    if (chevron) {
      chevron.classList.toggle("fa-caret-up", !isHidden);
      chevron.classList.toggle("fa-caret-down", isHidden);
    }
  };

  syncToggleState(filterDiv.classList.contains("is-hidden"));

  newToggleLink.addEventListener(
    "click",
    function (e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      syncToggleState(filterDiv.classList.toggle("is-hidden"));
    },
    true
  );
};

// =========================================================================
// Sorting control optimistic UI
// =========================================================================

// Sets up the dropdown menu part of the sorting control.
// (The inventory-sort-keys part uses `<a>` elements so they don't need extra setup.)
export const initializeSortingControl = () => {
  const sortControl = document.getElementById("inventory-sort-control");
  if (!sortControl) return;

  // Remove any existing event listeners to prevent duplicates
  const oldDropdownTrigger = sortControl.querySelector(".sort-menu-trigger");
  if (oldDropdownTrigger) {
    const newTrigger = oldDropdownTrigger.cloneNode(true);
    oldDropdownTrigger.parentNode.replaceChild(newTrigger, oldDropdownTrigger);
  }

  // Handle the three-dots dropdown with floating-ui
  const dropdownTrigger = sortControl.querySelector(".sort-menu-trigger");
  const dropdownMenu = sortControl.querySelector(".dropdown-menu.sort-dropdown-menu");

  // TODO: See if we can get rid of using floating-ui here, it might not be needed anymore.
  //  It WAS previously solving a problem, but a few things have changed, and the problem might have disappeared.
  if (dropdownTrigger && dropdownMenu) {
    const dropdownContainer = dropdownTrigger.closest(".dropdown");
    let cleanupAutoUpdate = null;

    // Reset styles that might have been set during loading
    dropdownTrigger.style.pointerEvents = "";
    dropdownTrigger.style.opacity = "";

    // Style the dropdown for floating-ui
    dropdownMenu.style.position = "absolute";
    dropdownMenu.style.zIndex = "9999";

    const updatePosition = () => {
      computePosition(dropdownTrigger, dropdownMenu, {
        placement: "bottom-end",
        strategy: "absolute",
        middleware: [offset(4), flip(), shift({ padding: 5 })],
      }).then(({ x, y }) => {
        Object.assign(dropdownMenu.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    };

    const showDropdown = () => {
      dropdownContainer.classList.add("is-active");
      dropdownMenu.style.display = "block";
      // Start autoUpdate for responsive positioning
      cleanupAutoUpdate = autoUpdate(dropdownTrigger, dropdownMenu, updatePosition);
    };

    const hideDropdown = () => {
      dropdownContainer.classList.remove("is-active");
      dropdownMenu.style.display = "none";
      // Clean up autoUpdate when hiding
      if (cleanupAutoUpdate) {
        cleanupAutoUpdate();
        cleanupAutoUpdate = null;
      }
    };

    // Click outside bound once per menu; cleans itself up if node is replaced
    once(dropdownMenu, "outside-click", () => {
      const outsideHandler = (e) => {
        if (!dropdownMenu.isConnected) {
          document.removeEventListener("click", outsideHandler);
          return;
        }
        if (!dropdownTrigger.contains(e.target) && !dropdownMenu.contains(e.target)) hideDropdown();
      };
      document.addEventListener("click", outsideHandler);
    });

    // Guarantee cleanup if a replace lands while open
    document.addEventListener("partial:replace", hideDropdown, { once: true });

    document.addEventListener("floating-toggle-clicked", hideDropdown);

    // Toggle on trigger click
    dropdownTrigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (dropdownContainer.classList.contains("is-active")) {
        hideDropdown();
      } else {
        showDropdown();
      }
    });

    once(dropdownMenu, "bind-sort-links", () => {
      dropdownMenu.addEventListener("click", (e) => {
        const link = e.target.closest("a[data-optimistic-sort]");
        if (!link) return;
        hideDropdown();
        dropdownTrigger.style.pointerEvents = "none";
        dropdownTrigger.style.opacity = "0.5";
      });
    });
  }

  if (dropdownTrigger && dropdownMenu) {
    once(sortControl, "bind-status-triggers", () => {
      const handleClick = (e) => {
        // Make the whole sort-controls area clickable, but ignore clicks on
        // the menu trigger, inside the dropdown menu, and on the direction arrow
        const inControls = e.target.closest(".sort-controls");
        if (!inControls) return;
        if (e.target.closest(".dropdown-menu.sort-dropdown-menu")) return;
        if (e.target.closest(".sort-menu-trigger")) return;
        if (e.target.closest(".sort-direction-arrow")) return;

        e.preventDefault();
        e.stopPropagation();
        dropdownTrigger.click();
      };

      const handleKeydown = (e) => {
        const inControls = e.target.closest(".sort-controls");
        if (!inControls) return;
        if (e.target.closest(".dropdown-menu.sort-dropdown-menu")) return;
        if (e.target.closest(".sort-menu-trigger")) return;
        if (e.target.closest(".sort-direction-arrow")) return;

        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          dropdownTrigger.click();
        }
      };

      sortControl.addEventListener("click", handleClick);
      sortControl.addEventListener("keydown", handleKeydown);
    });
  }

  // Handle sort direction toggle on arrow click; since we establish that pattern
  // in column headers, it would probably be frustrating if it didn't toggle in this case
  const sortArrow = sortControl.querySelector(".sort-direction-arrow");
  if (sortArrow) {
    const newArrow = sortArrow.cloneNode(true);
    sortArrow.parentNode.replaceChild(newArrow, sortArrow);

    newArrow.addEventListener("click", function (e) {
      e.stopPropagation();

      const activeItem = sortControl.querySelector(".sort-item.is-active");
      if (activeItem) {
        const arrowLink = activeItem.querySelector(`.sort-arrow.active`);
        if (arrowLink) {
          const oppositeLink = activeItem.querySelector(`.sort-arrow:not(.active)`);
          if (oppositeLink) {
            oppositeLink.click();
          }
        }
      }
    });
  }
};

function getInventoryLeftIndicatorClass(direction) {
  switch (direction) {
    case "asc":
      return "has-background-danger";
    case "mid":
      return "has-background-warning";
    case "desc":
      return "has-background-success";
    default:
      return "has-background-danger";
  }
}

const getSortParams = (urlString) => {
  const url = new URL(urlString, window.location.origin);
  const sortColumn = url.searchParams.get("sort_column");
  const sortDirection = url.searchParams.get("sort_direction");
  return { sortColumn, sortDirection };
};

// Optimistic UI update to say "Sorting by ..." while loading
// Note: if you're troubleshooting this, make sure to consider initializeUrlQueryParamHandling,
// as the sorting depends on getting query params from hrefs.
const updateSortDisplay = ({ sortControl, sortLabel, sortColumn, sortDirection }) => {
  const sortedByStatus = sortControl.querySelector("#sorted-by-status");
  const sortedByText = sortControl.querySelector("#sorted-by-status-text");
  const sortedByColumn = sortControl.querySelector("#sorted-by-status-column");

  if (sortedByStatus) {
    // Change text to "Sorting by" for optimistic update
    if (sortedByText) {
      sortedByText.textContent = "Sorting by";
    }

    if (sortedByColumn) {
      sortedByColumn.textContent = sortLabel;
    }

    if (sortColumn === "inventories.weeks_remaining") {
      const statusLabel = sortedByStatus.querySelector(".status-label");
      let indicator = sortedByStatus.querySelector(".inventory-remaining-indicator");
      if (!indicator && statusLabel) {
        indicator = document.createElement("span");
        indicator.className = "inventory-remaining-indicator";
        statusLabel.appendChild(indicator);
      }
      if (indicator) {
        indicator.className = "inventory-remaining-indicator";
        indicator.classList.add(getInventoryLeftIndicatorClass(sortDirection));
        indicator.style.display = "inline-block";
      }

      const arrow = sortedByStatus.querySelector(".status-arrow");
      if (arrow) arrow.remove();
    } else {
      let arrow = sortedByStatus.querySelector(".status-arrow");
      if (!arrow) {
        arrow = document.createElement("span");
        arrow.className = "status-arrow";
        sortedByStatus.querySelector(".status-label").appendChild(arrow);
      }
      arrow.textContent = sortDirection === "asc" ? "↑" : "↓";

      const indicator = sortedByStatus.querySelector(".inventory-remaining-indicator");
      if (indicator) indicator.remove();
    }
  }

  // Note: we don't update dropdown items optimistically since dropdown closes immediately

  const inventorySortButtons = sortControl.querySelectorAll(
    ".inventory-remaining-links .inventory-remaining-link"
  );
  inventorySortButtons.forEach((button) => {
    button.classList.remove("is-active");
  });

  if (sortColumn === "inventories.weeks_remaining") {
    const activeSortButton = sortControl.querySelector(
      `.inventory-remaining-link[href*="sort_column=${sortColumn}"][href*="sort_direction=${sortDirection}"]`
    );
    if (activeSortButton) {
      activeSortButton.classList.add("is-active");
    }
  }
};

function showSortingShimmer(sortControl) {
  sortControl.classList.add("is-loading-shimmer");

  const removeShimmer = () => {
    sortControl.classList.remove("is-loading-shimmer");
  };

  document.addEventListener("partial:replace", removeShimmer, { once: true });
  setTimeout(removeShimmer, 5000); // fallback
}

// Helper for partial:replace event handling
export const handlePartialReplace = () => {
  if (pendingUrlUpdate) {
    const url = new URL(pendingUrlUpdate, window.location.origin);
    window.history.replaceState({}, "", url.pathname + url.search);
    pendingUrlUpdate = null;
    updateFilterTagStates();
  }
};
